import type { ConvertDocumentRequest, MessageCard, StorageMutationOutcome, WebChatMessageToolCall } from '~/types/web-chat'
import { clientToolKind, type ConfirmId } from '~/composables/web-chat/dispatch'
import { buildConfirmCard, type CardBuildContext } from '~/composables/web-chat/confirmCardBuilders'
import { Tools, ToolKind, type ResumeTiming } from '~~/shared/web-chat/tools'
import type { ToolRun } from '#shared/tools/tool'
import { proposalFromReport, type WorkflowProposal } from '#shared/workflows/workflowProposal'

/**
 * Rebuilding a chat's cards from what was persisted — MAR-991. PURE: no store, no fetch.
 *
 * A confirm card used to exist only on the in-memory message, so a reload dropped every card: a
 * proposal the user had not answered vanished, and a finished one left no trace of what was done.
 * `remoteAgent.ts` now writes each confirm tool call as a `tool_calls` row — the model's input when
 * it is called, the browser's report when the paused session is resumed — and the conversation read
 * returns those rows on the message. This file turns one row back into the card the user saw.
 *
 * TWO RULES, both about not doing something twice:
 *
 *   * An ANSWERED call (the row has an output) comes back with NO `callId`. The session was resumed
 *     already; a card that still held the id would let a click resume it a second time.
 *   * An immediate-resume tool (`present_document_results`, `download_documents`, `convert_document`)
 *     never gets its id back at all: its session was answered with an ack the moment it rendered.
 *
 * `run_workflow` IS rebuilt here, as an UNCONFIRMED proposal (MAR-988). No `.docx` may exist before
 * the user confirms, so an unconfirmed proposal has no run to come back from; what the user did to it
 * (edited fields, a Cancelar) is saved onto this same row as `output.value.proposal`
 * (`saveWorkflowProposal`). A CONFIRMED proposal has a run, and `rehydrateWorkflowCards` replaces the
 * card built here with the run's (`restoredFromCall`).
 */

/** A persisted call that is a card, not an activity chip — every confirm client tool. */
export function isCardToolCall(tc: Pick<WebChatMessageToolCall, 'name'>): boolean {
  return clientToolKind(tc.name) === ToolKind.Confirm
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** What the browser reported for the call, or `undefined` while it is still unanswered. */
function reportedResponse(tc: WebChatMessageToolCall): Record<string, any> | undefined {
  const out = tc.output as unknown
  if (out == null) return undefined
  let value: unknown = out
  if (isRecord(out) && out.kind === 'json') value = out.value
  else if (isRecord(out) && out.kind === 'text') {
    try { value = JSON.parse(String(out.text)) } catch { return {} }
  }
  // An answer whose shape we cannot read is still an answer: the card settles, it does not reopen.
  return isRecord(value) ? value : {}
}

const count = (value: unknown) => (Array.isArray(value) ? value.length : 0)

function resumeTiming(name: string): ResumeTiming | undefined {
  return (Tools as Record<string, { resume?: ResumeTiming }>)[name]?.resume
}

/** The proposal saved on a persisted `run_workflow` call, if the user edited or declined it (MAR-988). */
export function savedWorkflowProposal(tc: WebChatMessageToolCall): WorkflowProposal | null {
  return tc.name === 'run_workflow' ? proposalFromReport(reportedResponse(tc)) : null
}

/**
 * A persisted `run_workflow` call → its proposal card, still loading its fields (the caller resolves
 * them against the workflow, from the saved values when there are any). Never carries `callId`: the
 * session was resumed the moment the card first rendered. `proposalCallId` is how it saves again.
 */
function workflowProposalCard(tc: WebChatMessageToolCall, ctx: CardBuildContext): MessageCard {
  const input = isRecord(tc.input) ? tc.input : {}
  const card = buildConfirmCard('run_workflow', input, undefined, ctx)
  if (card.toolId !== 'run_workflow') return card
  card.request.restoredFromCall = true
  if (tc.callId) card.request.proposalCallId = tc.callId
  if (savedWorkflowProposal(tc)?.status === 'declined') card.request.status = 'declined'
  return card
}

/**
 * The card one persisted confirm call renders, in the state the user left it. `null` for anything
 * that is not a card rebuilt from its call (a server tool, a silent client tool).
 */
export function cardFromToolCall(tc: WebChatMessageToolCall, ctx: CardBuildContext = { scopeLabel: '' }): MessageCard | null {
  if (!isCardToolCall(tc)) return null
  if (tc.name === 'run_workflow') return workflowProposalCard(tc, ctx)
  const response = reportedResponse(tc)
  const answered = response !== undefined
  const callId = !answered && resumeTiming(tc.name) === 'on-decision' ? tc.callId : undefined
  const input = isRecord(tc.input) ? tc.input : {}
  const card = buildConfirmCard(tc.name as ConfirmId, input, callId, ctx)
  if (!answered) return card

  if (response.declined === true) {
    if ('status' in card.request) (card.request as { status: string }).status = 'cancelled'
    if (card.toolId === 'file_to_case') card.request.loading = false
    return card
  }

  const ok = response.ok === true
  switch (card.toolId) {
    case 'delete_files':
      card.request.status = 'confirmed'
      card.request.outcome = { ok, succeeded: count(response.deleted), failed: count(response.failed) }
      break
    case 'move_files':
    case 'rename_files':
    case 'create_folder':
      card.request.status = 'confirmed'
      card.request.outcome = { ok } satisfies StorageMutationOutcome
      break
    case 'file_to_case':
      card.request.status = 'confirmed'
      card.request.outcome = { ok, succeeded: count(response.linked), failed: count(response.failed) }
      // The name the app resolved when the user accepted — reported back with the outcome.
      if (isRecord(response.case) && typeof response.case.name === 'string') card.request.caseName = response.case.name
      card.request.loading = false
      break
    case 'clear_chat_scope':
      card.request.status = 'confirmed'
      break
    default:
      // Offer cards and the conversion: the answer was an ack, not an outcome. A conversion's
      // outcome is its tool RUN — see `applyToolRunToConversion`.
      break
  }
  return card
}

/** A conversion card takes its state from the run that the user's confirm started. */
export function applyToolRunToConversion(request: ConvertDocumentRequest, run: ToolRun): void {
  request.runId = run.id
  request.status = run.status === 'succeeded' ? 'completed' : run.status === 'failed' ? 'failed' : 'running'
  request.outputItemId = run.output_item_id ?? null
  request.outputItemName = run.output_item_name ?? null
}

/** `Escaneo.PDF` → `PDF`. The source format the conversion card names; `undefined` without one. */
export function sourceFormatOf(fileName: string | null | undefined): string | undefined {
  const match = /\.([a-z0-9]{1,5})$/i.exec(String(fileName ?? '').trim())
  return match ? match[1]!.toUpperCase() : undefined
}
